import isArray from 'lodash/isArray';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { Observable, of, from } from 'rxjs';
import { retryWhen, delay, mergeMap, concatMap, toArray } from 'rxjs/operators';
import { inRange } from 'lodash';
import moment from 'moment';
import { set, differenceInMinutes } from 'date-fns';

import { Router, UrlTree, UrlSegment } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { ElementRef } from '@angular/core';

import { Session } from '@app/services/session';
import { NOT_DIGITS, PHONE_DIGITS_ONLY, NUMERIC } from '@app/services/custom-validators';
import { FORM_ELEMENT_KEYS } from '@app/misc/mobile-view-builder/templates/form-elements';

import { kmToMiles, milesToKm } from '@app/lib/utils';
import { format, getDay, isAfter, isBefore, parse, isEqual } from 'date-fns';
import { NzModalService, NzTableComponent } from 'ng-zorro-antd';
import { ILocation } from '@app/dashboard/fleet-view/fleet-view-map/history.service';
import {
  ICardMetadata,
  IServerCardMetadata,
  IServerCompanyCardSecuritySettings,
} from '@app/admin/fleet-card/card-management-v2/card-management.interface';
import { ICardHolderSpendProfile, ISpendProfile } from '@app/admin/fleet-card/shared/spend-controls/spendcontrols.interface';
import {
  ITransaction,
  IFuelLevelExceedsLimit,
  ITransactionIrregularity,
} from '@app/dashboard/fleet-cards/transactions/transactions.interface';
import { CustomerFleetSizeSegment } from './definitions';

import { IGraphData } from '@graphs/base-time-graph.component';
import { ADDRESS_DETAILS_KEYS, AMOUNT_REGEX, ATTEST_FORM_KEYS, INCORPORATION_DETAILS_KEYS } from '../card-application/card-application.constants';
import { CARD_ENTITIES, CARD_STATUS, CARDS_SECURITY_SETTINGS_FILTER_KEYS, FILTER_GENERAL_STATUS, FILTER_KEYS,
  FRAUD_CONTROL_STATUS, IMask, METADATA_FIELD_STATUS } from '../main.constants';
import {
  MESSAGES,
  ODOMETER_CONFIG_ID,
  irregularityReasonsKeys,
} from '@app/dashboard/fleet-cards/main.constants';
import { IShippingAddress } from '../orders/orders.interface';
import { IDriversRouteParams } from '../card-management-v2/card-management-v6.component';
import { ICustomizedCardsCount } from '@app/admin/fleet-card/settings/settings.interface';
import { ICustomFields, IFleetCardCompany } from './fleet-cards-waitlist/fleet-cards-waitlist.interface';
import { META_NON_COMPATIBLE_PHONE_KEYS } from '../settings/pre-transaction-metadata.service';
import { DAYS_OF_WEEK, TOOLTIP_TIME_FORMAT } from './spend-controls/main.constants';
import { II18nSingularPlural, SingularPluralsMap } from '@app/lib/i18n-backend-strings';

const CUSTOM_ERROR_CONTROLS: Set<string> = new Set([
  ADDRESS_DETAILS_KEYS.address1,
  ADDRESS_DETAILS_KEYS.address2,
  INCORPORATION_DETAILS_KEYS.incorporation_type,
]);

const decimalPipe = new DecimalPipe('en-US');

export const CARD_STYLE_CFS_LIMIT: number = 50;

export enum PARTNERS_LIST {
  NONE = 'none',
  BRIDGEHAUL = 'bridgehaul',
}

export enum OVERRIDE_STATUS_LIST {
  scheduled = 'scheduled',
  effective = 'effective',
  ended = 'ended',
}

export enum TEMP_DETAIL_DRAWER_TYPE {
  spendProfileOverride = 'spendProfileOverride',
  temporaryOverrideDifference = 'temporaryOverrideDifference',
}

export enum FLEET_CARD_PENDO_TRACKING_EVENTS_NAMES {
  AUTO_DECLINE_ABOVE_X_PERCENTAGE = 'Fleet Cards - Auto decline when above X%',
  VEHICLE_CARD_UNLOCK = 'Fleet Cards - Security Settings - Mobile Based Unlock - Vehicle Card',
}

export enum PENDO_EVENTS_AUTO_DECLINE_ABOVE_X_PERCENTAGE {
  FLEET_VIEW_SIGNALING = 'fleet_view_signaling',
  FUEL_LEVEL_CHANGED_CARD_LEVEL = 'fuel_level_changed_card_level_overrides',
  FUEL_LEVEL_CHANGED_ORGANIZATION_LEVEL = 'fuel_level_changed_organization_level',
  FUEL_LEVEL_CHANGED_TEMPORARY_OVERRIDES = 'fuel_level_changed_temporary_overrides',
  FUEL_LEVEL_CHANGED_BULK_CARDS_LEVEL = 'fuel_level_changed_bulk_cards_level_overrides',
}

export const FLEET_CARD_PENDO_TRACKING_EVENTS_DATA: { [key: string]: any } = {
  [FLEET_CARD_PENDO_TRACKING_EVENTS_NAMES.VEHICLE_CARD_UNLOCK]: {
    setting_level: 'Organization', // or 'Card' depending on usage
  },
};

export const DEFAULT_FUEL_LEVEL_DECLINE_PERCENTAGE: number = 70;

/**
 * converts IServerShippingAddress JSON object to string
 *
 * @param address - address in IShippingAddress format
 * @returns string representation of address
 *
 */
export function formatAddress(address: IShippingAddress): string {
  return [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.postalCode,
  ].filter(Boolean).join(', ');
}

/**
 * returns the url without query params
 *
 * @param url - url to parse, can be extracted using ```Router.url``` or from ```RouterEvent.url```
 * @param router - instance of ```Router``` to access parseUrl method
 * @returns url without params
 *
 */
export function getUrlWithoutParams(url: string, router: Router): string {
  const urlTree: UrlTree = router.parseUrl(url);
  const urlSegments: UrlSegment[] = urlTree.root.children.primary ? urlTree.root.children.primary.segments : [];
  const urlWithoutParams: string = urlSegments.map((segment: UrlSegment) => segment.path).join('/');
  return urlWithoutParams;
}

/**
 * Map for CFS Segment.
 * Segments are: ```smb```, ```commercial```, ```mid_market``` and ```enterprise```.
 * Note: the lower range limit is inclusive and the upper range limit is exclusive. So for 20 as lower range limit,
 * we will use 20 and for 80 as upper range limit, we will use 81.
 */
const cfsSegmentMap: Map<[number, number], CustomerFleetSizeSegment> = new Map([
  [[Number.NEGATIVE_INFINITY, 20], CustomerFleetSizeSegment.SMB],
  [[20, 81], CustomerFleetSizeSegment.COMMERCIAL],
  [[81, 501], CustomerFleetSizeSegment.MID_MARKET],
  [[501, Number.POSITIVE_INFINITY], CustomerFleetSizeSegment.ENTERPRISE],
]);

/**
 * returns the CFS segment for the user based on fleetsize
 *
 * @param fleetsize - Customer fleet size via ```session.getCompositeFleetSize``` method.
 * @returns CFS segment: ```smb```, ```commercial```, ```mid_market``` or ```enterprise```.
 *
 */
export function getCFSSegment(fleetsize: number): CustomerFleetSizeSegment {
  return Array.from(cfsSegmentMap.entries()).find(([key]) => inRange(fleetsize, key[0], key[1]))[1];
}

export function normaliseMicros(amountInMicros: number | string): number {
  return Number(amountInMicros) / 1000000;
}

export function denormaliseMicros(amount: number): number {
  return Number((amount * 1000000).toFixed());
}

export function customAmountValidator(control: FormControl): { invalid: true } | null {
  return control.value && !(control.value.match(AMOUNT_REGEX) || control.value.match(NUMERIC)) ? { invalid: true } : null;
}

export function customAttestationValidator(control: FormControl): { invalid: true } | null {
  return !!control.value ? null : { invalid: true };
}

export function daysFromToday(dateString: string): number {
  const currentDate = new Date();
  const diff = Math.abs(currentDate.getTime() - new Date(dateString).getTime());
  return Math.ceil(diff / (1000 * 3600 * 24));
}

export function generateFormElements(formKeys: any, formConfig: any): any {
  try {
    const formElements = Object.keys(formKeys).reduce((elements, key) => {
      const config = formConfig[key];
      const validators = [];
      let initialValue: any;
      if (config.required) {
        validators.push(Validators.required);
      }
      if (config.maxLength) {
        validators.push(Validators.maxLength(config.maxLength));
      }
      if (config.minLength) {
        validators.push(Validators.minLength(config.minLength));
      }
      if (config.pattern) {
        validators.push(Validators.pattern(config.pattern));
      }
      if (config.minValue) {
        validators.push(Validators.min(config.minValue));
      }
      if (config.type === FORM_ELEMENT_KEYS.TOGGLE) {
        initialValue = false;
      } else if (config.type === FORM_ELEMENT_KEYS.NUMBER) {
        validators.push(customAmountValidator);
      } else if (config.type === FORM_ELEMENT_KEYS.SIGNATURE) {
        validators.push(customAttestationValidator);
      }
      const control = new FormControl(initialValue, validators);
      if (config.groupName) {
        if (elements.hasOwnProperty(config.groupName)) {
          elements[config.groupName].addControl(key, control);
        } else {
          elements[config.groupName] = new FormGroup({ [key]: control });
        }
      } else {
        elements[key] = new FormControl(initialValue, validators);
      }
      return elements;
    }, {});
    return formElements;
  } catch (error) {
    console.error(error);
  }
}

export function getErrorTip(form: FormGroup, formConfig: any, controlName: string): string {
  const controlErrors: ValidationErrors = form.controls[controlName] && form.controls[controlName].errors;
  const isEmpty: boolean = controlErrors && controlErrors['required'];
  const invalid: boolean = controlErrors && controlErrors['invalid'];
  const maxLength: boolean = controlErrors && controlErrors['maxlength'];
  const minLength: boolean = controlErrors && controlErrors['minlength'];
  const minValue: boolean = controlErrors && controlErrors['min'];
  const pattern: boolean = controlErrors && controlErrors['pattern'];
  const customError: string = controlErrors && controlErrors['customError'];
  if (controlName === ATTEST_FORM_KEYS.consent && (isEmpty || invalid)) {
    return MESSAGES.CONSENT;
  } else if (CUSTOM_ERROR_CONTROLS.has(controlName) && customError) {
    return customError;
  }
  if (isEmpty || minValue) {
    return `${MESSAGES.INFO_IS_REQUIRED}`;
  } else if (invalid || maxLength || minLength || pattern) {
    return `${formConfig[controlName].label} ${MESSAGES.INVALID}`;
  }
}

export const FLEET_CARD_ADMIN_ROUTES = {
  CARD_APPLICATION: '/admin/fleet-cards/card-application',
  ACTIVATE_CARD: '/admin/fleet-cards/card-management/activate-card',
};

export function convertDollarsToCents(amount: number): number {
  return typeof amount !== 'number' ? amount : Math.round(amount * 100);
}

export function convertCentsToDollars(amount: number): number {
  return typeof amount !== 'number' ? amount : amount / 100;
}

export function parseNull(value: any): null | number {
  if (typeof value === 'number') return value;
  return value === '' || value === null ? null : Number(value.replaceAll(',', ''));
}

export function createSimpleNumberMask(integerLimit: number = null): IMask {
  return createNumberMask({
    includeThousandsSeparator: false,
    prefix: '',
    allowDecimal: false,
    integerLimit,
    allowLeadingZeroes: true,
  });
}

export function transformAmount(value: string | null): string | null {
  if (value) {
    const amount = restoreAmount(value);
    return decimalPipe.transform(amount, '1.2-3');
  }
  return null;
}

export function restoreAmount(value: any): string {
  return value ? parseFloat(value.replaceAll(',', '')).toString() : value;
}

export function formatEIN(ein: string | null): string | null {
  if (ein) {
    // remove all non-dash and non-numerals
    let sanitisedEin = ein.replace(/[^\d-]/g, '');

    // add a dash if number from the second group appears
    sanitisedEin = sanitisedEin.replace(/^(\d{2})-?(\d{1,7})/, '$1-$2');

    return sanitisedEin;
  }
  return null;
}

export function formatSSN(ssn: string | null): string | null {
  if (ssn) {
    // remove all non-dash and non-numerals
    let sanitisedSsn = ssn.replace(/[^\d-]/g, '');

    // add the first dash if number from the second group appear
    sanitisedSsn = sanitisedSsn.replace(/^(\d{3})-?(\d{1,2})/, '$1-$2');

    // add the second dash if numbers from the third group appear
    sanitisedSsn = sanitisedSsn.replace(/^(\d{3})-?(\d{2})-?(\d{1,4})/, '$1-$2-$3');

    return sanitisedSsn;
  }
  return null;
}

export function delayedRetry(delayTime: number, maxRetry: number) {
  let retries: number = maxRetry;
  return (src: Observable<any>) =>
    src.pipe(
      retryWhen((errors: Observable<any>) => errors.pipe(
        delay(delayTime),
        mergeMap(error => retries-- > 0 && of(error)),
      )),
    );
}

export function isConsecutiveDays(enableDays: number[]): boolean {
  const days = enableDays;
  let isConsecutive = false;
  for (let i = 0; i < days.length; i++) {
    if (days[i] !== days[i + 1] && days[i] == 1) {
      if (!isConsecutive) {
        isConsecutive = true;
      } else {
        isConsecutive = false;
        break;
      }
    }
  }
  return isConsecutive;
}

// This function is V2 of the function vehicleBasedActiveStatus
export function getCardsDerivedStatus(
  entityType: string,
  isLocked: boolean,
  securitySettingStatus: string,
  securitySettings: IServerCompanyCardSecuritySettings,
  metaDataStatus: string,
  featureFlags: any,
): string {
  // Fleet level security settings
  const { driver_card_security, vehicle_card_security, fleet_user_card_security } = securitySettings;
  const { isFleetCardsAutoDeclineTransactionM2PermanentCardOverridesEnabled } = featureFlags;
  let derivedStatus: string = '';

  // as BE (is_locked) has inplace logic for all valid security fields
  if (isLocked) {
    derivedStatus = CARD_STATUS.active_locked;
  } else {
    // If permanent card overrides are enabled, then do not consider Fleet level security settings
    const isOverrideAndCardSecuritySettingStatusActive = isFleetCardsAutoDeclineTransactionM2PermanentCardOverridesEnabled &&
      (securitySettingStatus === FRAUD_CONTROL_STATUS.active || metaDataStatus === FRAUD_CONTROL_STATUS.active);

    if (isOverrideAndCardSecuritySettingStatusActive) {
      derivedStatus = CARD_STATUS.active_unlocked;
    } else {
      const entitySecurityMap: Record<string, string> = {
        [CARD_ENTITIES.DRIVER]: driver_card_security,
        [CARD_ENTITIES.VEHICLE]: vehicle_card_security,
        [CARD_ENTITIES.FLEET_USER]: fleet_user_card_security,
      };
      const entitySecurityFleetLevel = entitySecurityMap[entityType];

      if (
        (entitySecurityFleetLevel === FRAUD_CONTROL_STATUS.active && securitySettingStatus === FRAUD_CONTROL_STATUS.active) ||
        metaDataStatus === FRAUD_CONTROL_STATUS.active
      ) {
        derivedStatus = CARD_STATUS.active_unlocked;
      }
    }
  }

  return derivedStatus;
}

export function vehicleBasedActiveStatus(entityType: string, locked: boolean,
  securitySettingStatus: string, securitySettings: IServerCompanyCardSecuritySettings, metaDataStatus: string): string {

  const { driver_card_security, vehicle_card_security } = securitySettings;

  // as BE (is_locked) has inplace logic for all valid security fields
  if (locked) return CARD_STATUS.active_locked;

  if (driver_card_security && vehicle_card_security) {
    if (entityType === 'driver') {
      if (securitySettingStatus === 'active' || metaDataStatus === 'active') {
        return locked ? CARD_STATUS.active_locked : CARD_STATUS.active_unlocked;
      } else {
        return '';
      }
    } else if (entityType === 'vehicle') {
      if (securitySettingStatus === 'active' || metaDataStatus === 'active') {
        return locked ? CARD_STATUS.active_locked : CARD_STATUS.active_unlocked;
      } else {
        return '';
      }
    }
  } else if (driver_card_security && !vehicle_card_security) {
    if (entityType === 'driver') {
      if (securitySettingStatus === 'active' || metaDataStatus === 'active') {
        return locked ? CARD_STATUS.active_locked : CARD_STATUS.active_unlocked;
      } else {
        return '';
      }
    } else if (entityType === 'vehicle') {
      return '';
    }
  } else if (!driver_card_security && vehicle_card_security) {
    if (entityType === 'driver') {
      return '';
    } else if (entityType === 'vehicle') {
      if (securitySettingStatus === 'active' || metaDataStatus === 'active') {
        return locked ? CARD_STATUS.active_locked : CARD_STATUS.active_unlocked;
      } else {
        return '';
      }
    }
  } else if (!driver_card_security && !vehicle_card_security && metaDataStatus === 'active') {
    return locked ? CARD_STATUS.active_locked : CARD_STATUS.active_unlocked;
  }
  return '';
}

export function getUTCTimeDifferenceFromNow(utcString: string) {
  const timeDifference: number = differenceInMinutes(new Date(utcString), new Date());
  const leastMinimumMinute: number = 0;
  return timeDifference > leastMinimumMinute ? timeDifference : leastMinimumMinute;
}

export function clearSessionStorage(keys: string[]): void {
  keys.forEach((key: string) => {
    if (sessionStorage.getItem(key)) sessionStorage.removeItem(key);
  });
}

export function mapCardMetaDataRecord(record: IServerCardMetadata, session: Session): ICardMetadata {
  let transformedValue: string = null;
  if (record.id === ODOMETER_CONFIG_ID) {
    transformedValue = calculateDistance(record.current_value, record.unit, session);
  }

  return {
    id: record.id,
    name: record.name,
    currentValue: record.current_value,
    subtext: record.subtext ?? null,
    valueType: record.value_type ?? null,
    unit: record.unit ?? null,
    possibleValues: record.values ?? null,
    viewName: record.name ?? null,
    viewValue: transformedValue ?? record.current_value ?? null,
  };
}

export function calculateDistance(distance: string, unit: string, session: Session): string {
  if (!distance || !unit) return null;
  const numericDistance = +distance;
  const isMetric = session.getUserMetricUnits();
  if ((isMetric && unit === 'km') || (!isMetric && unit === 'mi')) return distance;
  return Math.round(isMetric ? milesToKm(numericDistance) : kmToMiles(numericDistance)).toString();
}

export function checkSpendProfileTimeRestrictions(
  record: ICardHolderSpendProfile,
  showLockCardModal: () => void,
  nzModalService: NzModalService): void {

  if (!record?.enableDays?.length) {
    showLockCardModal();
    return;
  }
  const { enableDays } = record;

  let { enableStartTime, enableEndTime } = record;

  enableStartTime = enableStartTime ? enableStartTime : '00:00';
  enableEndTime = enableEndTime ? enableEndTime : '23:59';

  const currentDate = new Date();
  const currentDayIndex = getDay(currentDate);
  const nextEnabledDay = getNextEnabledDay(enableDays, currentDayIndex);

  const startTime = parse(enableStartTime, 'HH:mm', currentDate);
  const endTime = parse(enableEndTime, 'HH:mm', currentDate);
  const currentTime = parse(format(currentDate, 'HH:mm'), 'HH:mm', currentDate);

  if (enableDays[currentDayIndex] === 1 && isAfter(currentTime, startTime) && isBefore(currentTime, endTime)) {
    showLockCardModal();
  } else {
    nzModalService.create({
      nzWidth: 368,
      nzTitle: $localize`Unable to unlock`,
      nzContent: $localize`The selected card cannot be unlocked until ${getDayName(nextEnabledDay)} at ${format(startTime, 'h:mm a')} due to its spend profile settings.`,
      nzClosable: false,
      nzWrapClassName: 'vertical-center-modal',
      nzFooter: [{ label: $localize`Cancel`, onClick: () => nzModalService.closeAll(), nzAutofocus: null }],
    });
  }
}

export function getNextEnabledDay(enableDays: number[], currentDayIndex: number): number {
  for (let i = 1; i <= 6; i++) {
    const nextDayIndex: number = (currentDayIndex + i) % 7;
    if (enableDays[nextDayIndex] === 1) {
      return nextDayIndex;
    }
  }
  return currentDayIndex;
}

export function getDayName(dayIndex: number): string {
  const days: string[] = [
    $localize`Sunday`,
    $localize`Monday`,
    $localize`Tuesday`,
    $localize`Wednesday`,
    $localize`Thursday`,
    $localize`Friday`,
    $localize`Saturday`,
  ];
  return days[dayIndex];
}

export function findTransactionAndPushToChartData(
  filteredTransactions: ITransaction[],
  location: ILocation,
  previousLocation: ILocation,
  telematicChartData: IGraphData[],
  isLastLocation: boolean = false): void {
  const foundTransactions = filteredTransactions.filter((tr: ITransaction) => {
    const transactionTime: Date = new Date(tr.transactionTimeRaw);
    const locationTime: Date = new Date(location.locatedAt);
    const previousLocationTime: Date | null = previousLocation ? new Date(previousLocation.locatedAt) : null;

    // transactions after the last location
    if (isLastLocation) {
      return (isAfter(transactionTime, locationTime));
    }

    // transactions before first location
    if (!previousLocationTime) {
      return (isBefore(transactionTime, locationTime) || isEqual(transactionTime, locationTime));
    }

    // transactions between two locations
    return (isAfter(transactionTime, previousLocationTime) &&
      (isBefore(transactionTime, locationTime) || isEqual(transactionTime, locationTime)));
  });

  foundTransactions.map((transaction: ITransaction) => processFuelTelematicGraphTransaction(transaction, telematicChartData));
}

function processFuelTelematicGraphTransaction(transaction: ITransaction, telematicChartData: IGraphData[]): void {
  let previousFuelGal: number = 0;
  let transactionFuelGal: number = 0;
  let tankSizeEstimateGal: number = 1;
  let inferredFuelGal: number = 0;

  if (transaction.irregularityDetails) {
    const irregularityDetails: ITransactionIrregularity = transaction.irregularityDetails;
    if (irregularityDetails.fuelTankLevelMismatch) {
      previousFuelGal = irregularityDetails.fuelTankLevelMismatch.previousFuelGal || previousFuelGal;
      transactionFuelGal = irregularityDetails.fuelTankLevelMismatch.transactionFuelGal || transactionFuelGal;
      tankSizeEstimateGal = irregularityDetails.fuelTankLevelMismatch.tankSizeEstimateGal || tankSizeEstimateGal;
      inferredFuelGal = irregularityDetails.fuelTankLevelMismatch.inferredFuelGal || inferredFuelGal;
    }

    let expectedGalValue: number = previousFuelGal + transactionFuelGal;
    let actualGalValue: number = previousFuelGal + inferredFuelGal;
    let expectedGalPercentage: number = Math.min(Math.max((expectedGalValue / tankSizeEstimateGal) * 100, 0), 105);

    expectedGalValue = Math.max((expectedGalValue / tankSizeEstimateGal) * 100, 0);
    actualGalValue = Math.max((actualGalValue / tankSizeEstimateGal) * 100, 0);

    expectedGalValue = parseFloat(expectedGalValue.toFixed(1));
    actualGalValue = parseFloat(actualGalValue.toFixed(1));

    const fuelLevelExceedsLimit: IFuelLevelExceedsLimit = irregularityDetails.fuelLevelExceedsLimit;
    if (fuelLevelExceedsLimit && fuelLevelExceedsLimit.isFuelLevelExceedsLimit) {
      expectedGalValue = fuelLevelExceedsLimit.fuelLevelLimit;

      // we won't be getting the vehicle's fuel levels from transactions
      // so extracting the last known fuel level from telematic chart data (breadcrumbs)
      if (telematicChartData.length > 0) {
        const lastNonNullTelematicData: IGraphData = [...telematicChartData].reverse().find(
          (entry: IGraphData) => entry.primaryFuelLevel !== null && entry.primaryFuelLevel !== undefined,
        );
        actualGalValue = lastNonNullTelematicData ? lastNonNullTelematicData.primaryFuelLevel : actualGalValue;
        expectedGalPercentage = telematicChartData[telematicChartData.length - 1].primaryFuelLevel;
      }
    }

    telematicChartData.push(
      {
        date: new Date(transaction.transactionTimeRaw),
        transactionExpected: expectedGalPercentage,
        expectedGalValue,
        actualGalValue,
        isTransaction: true,
        irregularityReasons: irregularityDetails.irregularityReasons || [],
      },
    );
  }
}

export function getScrollOffsetHeight(headerWrapper: ElementRef, table: NzTableComponent): number {
  const headerWrapperOffsetHeight: number = headerWrapper?.nativeElement?.offsetHeight ?? 0;
  const tableHeaderOffsetHeight: number = table?.tableHeaderNativeElement?.scrollHeight ?? 0;
  return headerWrapperOffsetHeight + tableHeaderOffsetHeight;
}

export function convertFraudControlFilterToLocalMapping(
  params: IDriversRouteParams,
  isPermanentOverridesEnabled: boolean = false,
): string[] {
  const fraudControlsFilterMapping: string[] = [];

  const mobileBasedUnlock = params[FILTER_KEYS.MOBILE_BASED_UNLOCK];
  const flagFuelTransaction = params[FILTER_KEYS.FLAG_FUEL_TRANSACTION];
  const autoDeclineFuelTransaction = params[FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION];

  // TODO:
  // Auto decline - remove this proximityBasedDecline after
  // permanent overrides is GAed
  const proximityBasedDecline = params[FILTER_KEYS.PROXIMITY_BASED_DECLINE];

  if (isArray(mobileBasedUnlock)) {
    if (mobileBasedUnlock.includes(FILTER_GENERAL_STATUS.ENABLED)) {
      fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_ENABLED);
    }
    if (mobileBasedUnlock.includes(FILTER_GENERAL_STATUS.DISABLED)) {
      fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_DISABLED);
    }
  }

  if (isPermanentOverridesEnabled) {
    if (isArray(autoDeclineFuelTransaction)) {
      if (autoDeclineFuelTransaction.includes(FILTER_GENERAL_STATUS.ENABLED)) {
        fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION_ENABLED);
      }
      if (autoDeclineFuelTransaction.includes(FILTER_GENERAL_STATUS.DISABLED)) {
        fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION_DISABLED);
      }
    }

    if (isArray(flagFuelTransaction)) {
      if (flagFuelTransaction.includes(FILTER_GENERAL_STATUS.ENABLED)) {
        fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.FLAG_FUEL_TRANSACTION_ENABLED);
      }
      if (flagFuelTransaction.includes(FILTER_GENERAL_STATUS.DISABLED)) {
        fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.FLAG_FUEL_TRANSACTION_DISABLED);
      }
    }
  } else {
    // TODO:
    // Auto decline - remove this else block for proximityBasedDecline after
    // permanent overrides is GAed
    if (isArray(proximityBasedDecline)) {
      if (proximityBasedDecline.includes(FILTER_GENERAL_STATUS.ENABLED)) {
        fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.PROXIMITY_BASED_DECLINE_ENABLED);
      }
      if (proximityBasedDecline.includes(FILTER_GENERAL_STATUS.DISABLED)) {
        fraudControlsFilterMapping.push(CARDS_SECURITY_SETTINGS_FILTER_KEYS.PROXIMITY_BASED_DECLINE_DISABLED);
      }
    }
  }

  return fraudControlsFilterMapping;
}


/**
 * Processes an array of card management's 'Security' dropdown values.
 * @param {Map<string, any>} params - The Map to update.
 * @param {string} key - The key in the Map to update.
 * @param {string[]} values - The array of values to process.
 * If the array has more than one value, the key is removed from the Map.
 * If the array contains "ENABLED", the key is set to `true`.
 * If the array contains "DISABLED", the key is set to `false`.
 */
function processFilterArrayValues(params: Map<string, any>, key: string, values: string[]): void {
  if (values.length > 1) {
    params.delete(key);
  } else if (values.includes(FILTER_GENERAL_STATUS.ENABLED)) {
    params.set(key, true);
  } else if (values.includes(FILTER_GENERAL_STATUS.DISABLED)) {
    params.set(key, false);
  }
}

export function convertFraudControlFilterToRouteParams(params: Map<string, any>): Map<string, any> {
  const mobileBasedUnlock = params.get(FILTER_KEYS.MOBILE_BASED_UNLOCK);
  const proximityBasedDeclineTransactions = params.get(FILTER_KEYS.PROXIMITY_BASED_DECLINE);
  const flaggedFuelTransactions = params.get(FILTER_KEYS.FLAG_FUEL_TRANSACTION);
  const autoDeclineFuelTransaction = params.get(FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION);

  if (mobileBasedUnlock && isArray(mobileBasedUnlock)) {
    processFilterArrayValues(params, FILTER_KEYS.MOBILE_BASED_UNLOCK, mobileBasedUnlock);
  }

  if (proximityBasedDeclineTransactions && isArray(proximityBasedDeclineTransactions)) {
    processFilterArrayValues(params, FILTER_KEYS.PROXIMITY_BASED_DECLINE, proximityBasedDeclineTransactions);
  }

  if (flaggedFuelTransactions && isArray(flaggedFuelTransactions)) {
    processFilterArrayValues(params, FILTER_KEYS.FLAG_FUEL_TRANSACTION, flaggedFuelTransactions);
  }

  if (autoDeclineFuelTransaction && isArray(autoDeclineFuelTransaction)) {
    processFilterArrayValues(params, FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION, autoDeclineFuelTransaction);
  }

  return params;
}

export function getMobileBasedUnlockFilter(filter: string[]): string[] | undefined {
  if (isArray(filter)) {
    const filterValues = [];
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_ENABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.ENABLED);
    }
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.MOBILE_BASED_UNLOCK_DISABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.DISABLED);
    }
    return filterValues;
  }
  return;
}

export function getProximityBaseDeclineFilter(filter: string[]): string[] | undefined {
  if (isArray(filter)) {
    const filterValues = [];
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.PROXIMITY_BASED_DECLINE_ENABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.ENABLED);
    }
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.PROXIMITY_BASED_DECLINE_DISABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.DISABLED);
    }
    return filterValues;
  }
  return;
}

export function getAutoDeclineFuelTransactionFilter(filter: string[]): string[] | undefined {
  if (isArray(filter)) {
    const filterValues = [];
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION_ENABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.ENABLED);
    }
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.AUTO_DECLINE_FUEL_TRANSACTION_DISABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.DISABLED);
    }
    return filterValues;
  }
  return;
}

export function getFlagFuelTransactionFilter(filter: string[]): string[] | undefined {
  if (isArray(filter)) {
    const filterValues = [];
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.FLAG_FUEL_TRANSACTION_ENABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.ENABLED);
    }
    if (filter.includes(CARDS_SECURITY_SETTINGS_FILTER_KEYS.FLAG_FUEL_TRANSACTION_DISABLED)) {
      filterValues.push(FILTER_GENERAL_STATUS.DISABLED);
    }
    return filterValues;
  }
  return;
}

export function getIfOnlyVehicleIdRequired(companyData: IFleetCardCompany): boolean {
  return Object.entries(companyData?.transactionMetadata)?.every(([key, value]) => {
    if (!value) {
      return true;
    }

    if (key === META_NON_COMPATIBLE_PHONE_KEYS.VEHICLE_REPORTING) {
      return value.status === METADATA_FIELD_STATUS.ACTIVE;
    }

    if (Array.isArray(value)) {
      return value.every((field: ICustomFields) => field.status === METADATA_FIELD_STATUS.INACTIVE);
    }

    if (typeof value === 'string') {
      return value === METADATA_FIELD_STATUS.INACTIVE;
    }

    return value.status === METADATA_FIELD_STATUS.INACTIVE;
  });
}

/**
 * Converts a camelCase string to a format where the first word is capitalized
 * and all subsequent words are in lowercase, with spaces separating the words.
 *
 * @param {string} input - The camelCase string to be converted.
 * @returns {string} - The converted string with only the first word capitalized
 *                     and the rest of the words in lowercase.
 *
 * @example
 * // returns "Profile details"
 * camelCaseToTitleCase("profileDetails");
 */
export function camelCaseToTitleCase(input: string): string {
  if (!input) return input; // Return the input if it's empty or undefined

  // Convert camelCase to title case with spaces
  return input.replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before uppercase letters
    .toLowerCase() // Convert all characters to lowercase
    .replace(/^\w/, c => c.toUpperCase()); // Capitalize the first character
}

/**
 * Converts a time string from 24-hour format to 12-hour format.
 *
 * @param time24 - A string representing the time in 24-hour format (e.g., '23:59').
 * @returns A string representing the time in 12-hour format with AM/PM (e.g., '11:59 PM').
 *
 * @example
 * // Example usage:
 * const time24 = '23:59';
 * const time12 = convert24To12Hour(time24);
 * console.log(time12); // Output: "11:59 PM"
 */
export function convert24To12Hour(time24: string): string {
  if (!time24) return '';
  return format(parse(time24, 'HH:mm', new Date()), 'h:mm a');
}

export function getHighFlaggingVehicleListFileName(): string {
  const today: string = moment().format('MMDDYYYY');
  return `${today}_high_flagging_vehicle_list.csv`;
}

export function getExcludedVehicleListFileName(): string {
  const today: string = moment().format('MMDDYYYY');
  return `${today}_excluded_vehicle_list.csv`;
}

/**
 * Combines the date portion of one Date object with the time portion of another.
 *
 * @param date - The Date object providing the date portion.
 * @param time - The Date object providing the time portion.
 * @returns A new Date object with the combined date and time values.
 *
 * @example
 * const date = new Date('2025-01-16');
 * const time = new Date('1970-01-01T15:30:00');
 * const result = mergeDateAndTime(date, time);
 * console.log(result); // Outputs: 2025-01-16T15:30:00.000Z
 */
export function mergeDateAndTime(date: Date, time: Date): Date {
  return set(date, {
    hours: time.getHours(),
    minutes: time.getMinutes(),
    seconds: time.getSeconds(),
    milliseconds: time.getMilliseconds(),
  });
}

/**
 * Generates a textual representation of the time limits based on the provided spend profile.
 *
 * @returns A string representing the time limits in a human-readable format.
 *
 * The function formats the start and end times using the `TOOLTIP_TIME_FORMAT` and constructs a string that describes
 * the days and times during which the spend profile is enabled. It handles different cases such as:
 * - Every day
 * - Consecutive days
 * - Specific days
 *
 * The resulting string is localized using the `$localize` tag.
 */
export function getSpendProfileTimeLimitText(spendProfile: ICardHolderSpendProfile | ISpendProfile): string {
  let timeLimitText: string = '';
  const i18nSingularPluralsMap: II18nSingularPlural = SingularPluralsMap;

  const startTime = moment(spendProfile.enableStartTime, TOOLTIP_TIME_FORMAT.input).format(TOOLTIP_TIME_FORMAT.output);
  const endTime = moment(spendProfile.enableEndTime, TOOLTIP_TIME_FORMAT.input).format(TOOLTIP_TIME_FORMAT.output);
  const daysString = spendProfile.enableDays.join('');
  const startIndex = daysString.indexOf('1');
  const endIndex = daysString.lastIndexOf('1');
  if (!spendProfile.enableDays.includes(0)) {
    timeLimitText = $localize`Every day`;
  } else if (isConsecutiveDays(spendProfile.enableDays)) {
    const day = i18nSingularPluralsMap[DAYS_OF_WEEK[startIndex].id].plural;
    timeLimitText = startIndex == endIndex ?
      $localize`Only on ${day}` :
      $localize`${DAYS_OF_WEEK[startIndex].label} to ${DAYS_OF_WEEK[endIndex].label}`;
  } else {
    timeLimitText = $localize`Every `;
    spendProfile.enableDays.map((day: number, i: number) => {
      if (day) {
        timeLimitText += i !== endIndex ?
          `${DAYS_OF_WEEK[i].label}, ` : $localize`and ${DAYS_OF_WEEK[i].label}`;
      }
    });
  }
  timeLimitText += $localize` from ${startTime} to ${endTime}`;

  return timeLimitText;
}


function getCurrentDate() {
  return new Date();
}

export function overrideStartTime(startTime: string): Date {
  return new Date(startTime);
}

export function overrideEndTime(endTime: string): Date {
  return new Date(endTime);
}

export function isOverrideScheduled(startTime: string, endTime: string): boolean {
  return getCurrentDate() < overrideStartTime(startTime) && getCurrentDate() < overrideEndTime(endTime);
}

export function isOverrideEffective(startTime: string, endTime: string): boolean {
  return getCurrentDate() > overrideStartTime(startTime) && getCurrentDate() < overrideEndTime(endTime);
}

export function isOverrideEnded(endTime: string): boolean {
  return getCurrentDate() > overrideEndTime(endTime);
}

export function getOverrideStatus(startTime: string, endTime: string): string {
  if (isOverrideScheduled(startTime, endTime)) return OVERRIDE_STATUS_LIST.scheduled;
  if (isOverrideEffective(startTime, endTime)) return OVERRIDE_STATUS_LIST.effective;
  if (isOverrideEnded(endTime)) return OVERRIDE_STATUS_LIST.ended;
  return undefined;
}

export function formatAmount(amount?: number): number {
  return amount ? convertCentsToDollars(amount) : 0;
}

export function isCustomDays(enableDays: number[], enableStartTime: string, enableEndTime: string): boolean {
  return enableDays?.includes(0) || !!enableStartTime || !!enableEndTime;
}

export function customDayText(enableDays: number[], enableStartTime: string, enableEndTime: string): string {
  const enabledDaysText: string = getEnabledDaysFromValues(enableDays);
  if (!enableStartTime || !enableEndTime) return enabledDaysText;
  return `${enabledDaysText} ${$localize`From ${convert24To12Hour(enableStartTime)} to ${convert24To12Hour(enableEndTime)}`}`;
}

// Memoized function to get enabled days as text
export function getEnabledDaysFromValues(values: number[]): string {
  const ranges: string[] = [];
  let start: number | null = null;

  for (let i = 0; i < DAYS_OF_WEEK.length; i++) {
    if (values[i] === 1) {
      if (start === null) start = i;
    } else if (start !== null) {
      ranges.push(formatRange(start, i - 1));
      start = null;
    }
  }

  if (start !== null) {
    ranges.push(formatRange(start, DAYS_OF_WEEK.length - 1));
  }

  return joinRanges(ranges);
}

// Helper function to format a range of days
// We need this when selected days are contiguous
// example response: Monday to Friday
export function formatRange(start: number, end: number): string {
  return start === end
    ? DAYS_OF_WEEK[start].label
    : `${DAYS_OF_WEEK[start].label} to ${DAYS_OF_WEEK[end].label}`;
}

// function to join the ranges with commas and 'and'
// We need this when selected days are non-contiguous
// example response: Monday, Wednesday and Sunday
export function joinRanges(ranges: string[]): string {
  if (ranges.length === 0) return '';
  if (ranges.length === 1) return ranges?.[0];
  const lastRange = ranges.pop();
  return `${ranges.join(', ')} and ${lastRange}`;
}

/**
 * Formats a phone number string by removing non-digit characters and
 * arranging the digits in a specific format.
 *
 * @param value - The phone number string to format.
 * @returns The formatted phone number string. If the input does not match
 *          the expected format, the original value is returned.
 */
export function formatPhoneNumber(value: string): string {
  if (!value || value.length === 0) return null;

  const numberArray = value.replace(NOT_DIGITS, '').match(PHONE_DIGITS_ONLY)?.filter(phoneNumber => phoneNumber);
  if (numberArray) {
    let phoneNumber = '';
    if (numberArray.length === 5) {
      numberArray.shift();
      phoneNumber = '+' + numberArray[0] + ' ';
    }
    phoneNumber += numberArray[1] + '-' + numberArray[2] + '-' + numberArray[3];
    return phoneNumber;
  }
  return value;
}

/**
 * Filters the transactions to return only those with irregularities.
 *
 * @param transactions - An array of transactions to be filtered.
 * @returns An array of transactions that have irregularities.
 */
export function getTransactionWithIrregularities(transactions: ITransaction[]): ITransaction[] {
  return transactions.filter((transaction: ITransaction) => {
    const irregularityReasons: string[] = transaction.irregularityDetails?.irregularityReasons;
    return irregularityReasons.includes(irregularityReasonsKeys.fuelTankLevelMismatch) ||
      irregularityReasons.includes(irregularityReasonsKeys.fuelLevelExceedsLimit);
  });
}

export function sequentialApiCalls(observables: Array<Observable<any>>): Observable<any[]> {
  return from(observables).pipe(
    concatMap(observable => observable),
    toArray(),
  );
}

export function markInvalidFields(formGroup: FormGroup): void {
  for (const i of Object.keys(formGroup.controls)) {
    const control: AbstractControl = formGroup.controls[i] as AbstractControl;
    control.markAsDirty();
    control.markAllAsTouched();
    control.updateValueAndValidity();
  }
}

export function customizedCardsCountStatus(customizedCardsCount: ICustomizedCardsCount): string {
  let status: string = '';
  const driversCount: number = customizedCardsCount.drivers;
  const vehiclesCount: number = customizedCardsCount.vehicles;
  const fleetUsersCount: number = customizedCardsCount.fleetUsers;

  // Note:
  // - The text is not being translated directly because a language might place the count after the pronoun
  // - Condition is not applied directly to 's' because the plural forms in other languages might not differ with 1 character
  if (driversCount > 0) {
    status += $localize`${driversCount} ${driversCount > 1 ? 'drivers' : 'driver'}`
  }

  if (vehiclesCount > 0) {
    status += status.length > 0 ? ', ' : '';
    status += $localize`${vehiclesCount} ${vehiclesCount > 1 ? 'vehicles' : 'vehicle'}`
  }

  if (fleetUsersCount > 0) {
    status += status.length > 0 ? ', ' : '';
    status += $localize`${fleetUsersCount} ${fleetUsersCount > 1 ? 'fleet users' : 'fleet user'}`
  }

  return status;
}

// adding here to avoid multiple translation units
export const ADDRESS_VERIFICATION_DRIVERS_LICENSE_COPY: string =
  $localize`To verify this new address, please provide: 1) a copy of the requestor's driver's license, and
    2) one unaltered document showing both the business name and address.
    Acceptable documents include bank statements, utility bills, insurance statements, or signed lease agreements.`;

// adding here to avoid multiple translation units
export const ADDRESS_VERIFICATION_NON_DRIVERS_LICENSE_COPY: string =
  $localize`To process this order, you must provide at least one unaltered document containing both
    the business name and business address. Acceptable documents include: bank statements, utility bills,
    lease/rental agreements <i>(with signature page)</i> or insurance statements.`;

export function showDriverLicenseCopyInOrderConfirmation(compositeFleetSize: number, isFirstOrder: boolean): boolean {
  // the limits are defined by product/design
  return (compositeFleetSize >= 1 && compositeFleetSize <= 9) || (compositeFleetSize >= 10 && isFirstOrder);
}

export const CUSTOMIZED_CARDS_CFS_LIMIT = 50;

export function getCustomizedCardsCSVFileName(orderNumber: string): string {
  return `personalization_details_${orderNumber}.csv`;
}

export function getUniqueEntitiesBasedOnId(items: any): any {
  return items.reduce((acc, current) => {
    const x = acc.find(item => item.id === current.id);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, [])
}